import type { Garage, HoverPoint } from '../../types'
import type {
    ClusterAggregateProperties,
    ClusterFeature,
    GarageClusterFeature,
    GaragePointProperties,
} from './types'

export const superClusterOptions: {
    radius: number,
    maxZoom: number,
    map: (
        point: GaragePointProperties,
    ) => ClusterAggregateProperties,
    reduce: (
        acc: ClusterAggregateProperties,
        point: ClusterAggregateProperties,
    ) => ClusterAggregateProperties,
} = {
    radius: 75, // 75 is the optimal value for less overlap
    maxZoom: 14, // 17 is the optimal value for less overlap
    map: point => ({
        available: point.garage.available,
    }),
    reduce: (acc, point) => {
        acc.available = (acc.available || point.available) ? true : false
        return acc
    },
}

export const SEARCH_ZOOM_FALLBACK = 15
export const SEARCH_ZOOM_MAX = 16

export const isValidHoverPoint = (point: HoverPoint | null | undefined): point is HoverPoint =>
    typeof point?.lat === 'number' &&
    Number.isFinite(point.lat) &&
    typeof point?.lng === 'number' &&
    Number.isFinite(point.lng)

const toRadians = (value: number) => value * (Math.PI / 180)

const distanceMeters = (
    pointA: Pick<HoverPoint, 'lat' | 'lng'>,
    pointB: Pick<Garage, 'lat' | 'lng'>,
) => {
    const earthRadiusMeters = 6371000
    const latDelta = toRadians(pointB.lat - pointA.lat)
    const lngDelta = toRadians(pointB.lng - pointA.lng)
    const latA = toRadians(pointA.lat)
    const latB = toRadians(pointB.lat)
    const haversine =
        Math.sin(latDelta / 2) ** 2 +
        Math.cos(latA) * Math.cos(latB) * Math.sin(lngDelta / 2) ** 2
    return 2 * earthRadiusMeters * Math.asin(Math.sqrt(haversine))
}

const getNearestGarage = (
    searchPoint: HoverPoint,
    garageList: Array<Garage>,
) => {
    let nearestGarage: Garage | null = null
    let nearestDistance = Number.POSITIVE_INFINITY

    for (const garage of garageList) {
        const currentDistance = distanceMeters(searchPoint, garage)
        if (currentDistance < nearestDistance) {
            nearestDistance = currentDistance
            nearestGarage = garage
        }
    }

    return nearestGarage
}

export const getBoundsPadding = () =>
    window.innerWidth >= 900
        ? (window.innerWidth - 480) * 0.3
        : window.innerWidth * 0.2

export const applySearchFallbackViewport = (map: google.maps.Map, searchPoint: HoverPoint) => {
    map.panTo({ lat: searchPoint.lat, lng: searchPoint.lng })
    map.setZoom(Math.min(SEARCH_ZOOM_FALLBACK, SEARCH_ZOOM_MAX))
}

export const applySearchNearestGarageViewport = (
    map: google.maps.Map,
    searchPoint: HoverPoint,
    garageList: Array<Garage>,
) => {
    const nearestGarage = getNearestGarage(searchPoint, garageList)
    if (!nearestGarage) {
        applySearchFallbackViewport(map, searchPoint)
        return
    }

    const searchBounds = new google.maps.LatLngBounds()
    searchBounds.extend({ lat: searchPoint.lat, lng: searchPoint.lng })
    searchBounds.extend({ lat: nearestGarage.lat, lng: nearestGarage.lng })
    map.fitBounds(searchBounds, { left: getBoundsPadding(), right: getBoundsPadding() })

    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        const currentZoom = map.getZoom()
        if (typeof currentZoom === 'number' && currentZoom > SEARCH_ZOOM_MAX) {
            map.setZoom(SEARCH_ZOOM_MAX)
        }
    })
}

export function isClusterFeature(cluster: ClusterFeature): cluster is GarageClusterFeature {
    return cluster.properties.cluster
}
